<template>
	<PaymentSummaryCard
		:paymentSummary="filteredPaymentSummary"
		:showDetails
	/>
</template>

<script setup lang="ts">
import { formatPrice } from "~/helpers/formatting/price";
import { useStore } from "vuex";
import {
	productIsEligibleForDownPayment,
	getValidatedSubscriptionPrice,
} from "~/helpers/ecommerce/hardwareOrderHelper";
import type { PaymentSummary, PaymentSummaryLine } from "~/components/ecommerce/types/PaymentSummary";

const { terminals = [], showDetails = false } = defineProps<{ terminals?: any[]; showDetails?: boolean }>();

const store = useStore();

const { totalToPayNow, totalSaved, getPayNowPrices } = usePayNowPrices();

const shoppingCart = computed(() =>
	terminals.length > 0 ? terminals : store.getters["shoppingCart/visibleCartItems"],
);

const { phoneData, estimate, tradeInBonus } = useTradeIn(shoppingCart.value);
const baseProduct = computed(() => shoppingCart.value.find((item: any) => item.type === "hardware"));
const visibleSubscriptionsInCart = computed(() =>
	shoppingCart.value?.[0]?.subItems?.filter(
		(item: { type: string }) => item.type === "subscription" || item.type === "msisdn",
	),
);

const subscriptions = computed<any[]>(() => store.getters["shoppingCart/subscriptions"]);

const isExistingSubscription = (item: { type: string; metadata: { existingSubscription: any } }) =>
	item?.type === "subscription" && item?.metadata?.existingSubscription;

const validatedSubscriptionPrices = computed(() =>
	getValidatedSubscriptionPrice(subscriptions.value, visibleSubscriptionsInCart.value),
);

const insurance = computed(() => baseProduct.value?.subItems?.find((item: any) => item.type === "agreement"));
const subscription = computed(() => baseProduct.value?.subItems?.find((item: any) => item.type === "subscription"));

const allHardwareProducts = computed<any[]>(() =>
	shoppingCart.value
		.map((item: { subItems: any }) => [item, ...(item?.subItems ?? [])])
		.flat()
		.filter((item: { type: string }) => item?.type == "hardware"),
);

const totalMonthly = computed<number>(() => {
	const getCorrectMonthlyPrice = (item: { metadata: any; type: any }) => {
		if (productIsEligibleForDownPayment(item)) {
			return item?.metadata?.downPayment ? item?.metadata?.downPaymentPlan?.prices?.monthlyListPrice.value : 0;
		} else {
			if (isExistingSubscription(item)) {
				return 0;
			} else if (item.type === "subscription") {
				if (validatedSubscriptionPrices.value?.originalPrice > 0) {
					return validatedSubscriptionPrices.value?.discountedPrice || validatedSubscriptionPrices.value?.originalPrice;
				} else {
					return item.metadata?.showFallback || item.metadata?.recurringPrice || 0;
				}
			} else {
				return item.metadata?.saleRecurringPrice || item.metadata?.recurringPrice || 0;
			}
		}
	};
	return shoppingCart.value
		.filter((item: { type: string }) => item?.type !== "old-swap-device")
		.map((item: { subItems: any }) => [item, ...(item?.subItems ?? [])])
		.flat()
		.map(getCorrectMonthlyPrice)
		.reduce((a: any, b: any) => a + b, 0);
});

const paymentSummary = computed<PaymentSummary>(() => {
	const summary = {
		totalMonthly: totalMonthly.value,
		totalToPayNow: totalToPayNow.value,
		totalSaved: totalSaved.value,
		paymentLines: [
			{
				id: "TRADE-IN",
				name: "Innbytte",
				description: phoneData.value?.name || undefined,
				price: `- ${formatPrice.oneTime(estimate.value)}`,
				show: estimate?.value && baseProduct.value?.metadata?.downPayment,
			},
			{
				id: "TRADE-IN-BONUS",
				name: "Innbytte-bonus",
				description: undefined,
				price: `- ${formatPrice.oneTime(tradeInBonus.value)}`,
				show: tradeInBonus.value && estimate?.value && baseProduct.value?.metadata?.downPayment,
			},

			{
				id: "INSURANCE",
				name: insurance.value?.metadata?.name,
				description: undefined,
				price: formatPrice.monthly(insurance.value?.metadata?.recurringPrice),
				show: insurance.value,
			},
			{
				id: "SUBSCRIPTION",
				name: subscription.value?.metadata?.name,
				description: undefined,
				price: formatPrice.monthly(subscription.value?.metadata?.recurringPrice),
				show: subscription.value && !isExistingSubscription(subscription.value),
			},
			...allHardwareProducts.value.map((item) => getPaymentLine(item)),
		],
	};

	return summary;
});

const getPaymentLine = (item: any): PaymentSummaryLine => {
	const isDownPayment = item.metadata?.downPayment;
	const amountUpFront: number | undefined = isDownPayment
		? item.metadata?.downPaymentPlan?.amountUpFront || 0
		: undefined;
	const monthlyPrice = isDownPayment
		? formatPrice.monthly(item.metadata?.downPaymentPlan?.prices?.monthlyListPrice?.value)
		: undefined;
	const { price, beforePrice } = getPayNowPrices(item);

	const totalPrices = isDownPayment ? getPayNowPrices(item, { ignoreDownPayment: true }) : undefined; // For down payment we also need to compute the total prices

	return {
		id: item.id,
		name: item.metadata?.name,
		description: amountUpFront ? "Valgfritt kontantbeløp" : undefined,
		price: monthlyPrice || formatPrice.oneTime(price),
		subPrice: amountUpFront ? formatPrice.oneTime(amountUpFront) : undefined,
		beforePrice: beforePrice ? formatPrice.oneTime(beforePrice) : undefined,
		totalPrice: totalPrices?.price ? formatPrice.oneTime(totalPrices.price) : undefined,
		beforeTotalPrice: totalPrices?.beforePrice ? formatPrice.oneTime(totalPrices.beforePrice) : undefined,
		show: true,
	};
};

const filteredPaymentSummary = computed<PaymentSummary>(() => ({
	totalMonthly: paymentSummary.value.totalMonthly,
	totalToPayNow: paymentSummary.value.totalToPayNow,
	totalSaved: paymentSummary.value.totalSaved,
	paymentLines: paymentSummary.value.paymentLines?.filter((line) => line?.show),
}));
</script>
